/**
 * Utilitário para filtros de busca sem acentos
 * Centraliza a lógica de normalização de texto para components Select
 */

/**
 * Remove acentos e normaliza texto para busca
 * @param {String} str - Texto para normalizar
 * @return {String} Texto sem acentos em minúsculas
 */
const removeAccents = (str) => {
	if (!str || typeof str !== "string") return "";

	return str
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase();
};

/**
 * Função de filtro customizada para Select do Ant Design
 * Funciona com e sem acentos, case-insensitive
 * @param {String} input - Texto digitado pelo usuário
 * @param {Object} option - Opção do Select (deve ter children)
 * @return {Boolean} Se a opção deve aparecer nos resultados
 */
const customFilterOption = (input, option) => {
	if (!input) return true;
	if (!option || !option.children) return false;

	const inputNormalized = removeAccents(input);
	const optionTextNormalized = removeAccents(option.children);

	return optionTextNormalized.indexOf(inputNormalized) >= 0;
};

/**
 * Função de filtro para casos onde o texto está em uma propriedade específica
 * @param {String} textProperty - Nome da propriedade que contém o texto (ex: 'label', 'title')
 * @return {Function} Função de filtro
 */
const createCustomFilterOption = (textProperty = "children") => {
	return (input, option) => {
		if (!input) return true;
		if (!option) return false;

		const text =
			textProperty === "children"
				? option.children
				: option[textProperty];
		if (!text) return false;

		const inputNormalized = removeAccents(input);
		const optionTextNormalized = removeAccents(text);

		return optionTextNormalized.indexOf(inputNormalized) >= 0;
	};
};

/**
 * Função de filtro para busca em múltiplas propriedades
 * @param {Array} properties - Array de propriedades para buscar (ex: ['nome', 'codigo'])
 * @return {Function} Função de filtro
 */
const createMultiPropertyFilterOption = (properties = ["children"]) => {
	return (input, option) => {
		if (!input) return true;
		if (!option) return false;

		const inputNormalized = removeAccents(input);

		return properties.some((property) => {
			const text =
				property === "children" ? option.children : option[property];
			if (!text) return false;

			const optionTextNormalized = removeAccents(text);
			return optionTextNormalized.indexOf(inputNormalized) >= 0;
		});
	};
};

export {
	removeAccents,
	customFilterOption,
	createCustomFilterOption,
	createMultiPropertyFilterOption,
};

export default {
	removeAccents,
	customFilterOption,
	createCustomFilterOption,
	createMultiPropertyFilterOption,
};
